import type { Step3Props } from "../../model/propertiesTypes";
import { useStep3ViewModel } from "../../viewmodel/useStep3ViewModel";
import styles from "./Step3.module.css";
import andyHdLogo from "@/assets/andyhdlogo.png";

/**
 * Step 3: User Type
 * Choose whether you are the owner or tenant
 */
export const Step3 = ({ addressData, propertyDetails, formData, onNext, onBack }: Step3Props) => {
  const {
    userType,
    setUserType,
    isMailingAddress,
    setIsMailingAddress,
    isSubmitting,
    addressString,
    savingsAmount,
    handleSubmit,
    handleBackClick,
  } = useStep3ViewModel({ addressData, propertyDetails, formData, onNext, onBack });

  return (
    <div className={styles.step3Page}>
      {/* Header Section */}
      <header className={styles.header}>
        <button className={styles.backButton} onClick={handleBackClick} aria-label="Go back">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className={styles.logoContainer}>
          <div className={styles.logoCircle}>
            <img src={andyHdLogo} alt="Andy logo" className={styles.logoImage} />
          </div>
          <span className={styles.logoText}>ANDY</span>
        </div>

        <p className={styles.description}>
          It&apos;s about you and your property, having impact your final savings results such as
          exemptions, property condition, etc.
        </p>

        {/* Progress Bar */}
        <div className={styles.progressBar}>
          <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
            <div className={styles.progressStepIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" fill="#10b981" stroke="#10b981" strokeWidth="2" />
                <path
                  d="M6 10l3 3 5-6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.progressStepText}>Estimation Savings</span>
          </div>
          <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
            <div className={styles.progressStepIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" fill="#10b981" stroke="#10b981" strokeWidth="2" />
                <path
                  d="M6 10l3 3 5-6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.progressStepText}>Owner Details</span>
          </div>
          <div className={`${styles.progressStep} ${styles.progressStepActive}`}>
            <div className={styles.progressStepIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" fill="#10b981" stroke="#10b981" strokeWidth="2" />
                <path
                  d="M6 10l3 3 5-6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.progressStepText}>User Type</span>
          </div>
        </div>

        {/* Step Number Indicator - Large "03" */}
        <div className={styles.stepNumber}>03</div>
      </header>

      {/* Main Content */}
      <div className={styles.contentWrapper}>
        <div className={styles.contentContainer}>
          {/* Savings Card with Background SVG - Now on LEFT */}
          <div className={styles.savingsCardWrapper}>
            <div className={styles.decorativeShape}>
              <svg
                width="521"
                height="371"
                viewBox="0 0 521 371"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M438.392 0C414.109 2.24179e-06 438.8 45.6137 419.177 60.1327C399.535 74.6663 356.998 60.1327 356.998 60.1327C356.998 60.1327 399.804 73.7954 407.302 97.2967C414.796 120.783 388.088 157.429 388.088 157.429C388.088 157.429 414.119 120.265 438.392 120.265C462.665 120.265 469.051 171.943 488.696 157.429C508.342 142.916 461.989 120.783 469.482 97.2967C476.98 73.7954 527.29 83.616 519.786 60.1327C512.283 36.6494 477.249 74.6663 457.607 60.1327C437.984 45.6137 462.675 -2.24179e-06 438.392 0Z"
                  fill="#D9E5FF"
                />
                <path
                  d="M449 162C449 189.446 443.594 216.624 433.091 241.981C422.588 267.338 407.193 290.378 387.785 309.785C368.378 329.193 345.338 344.588 319.981 355.091C294.624 365.594 267.446 371 240 371C212.554 371 185.376 365.594 160.019 355.091C134.662 344.588 111.622 329.193 92.2147 309.785C72.8072 290.378 57.4124 267.338 46.9092 241.981C36.4059 216.624 31 189.446 31 162L449 162Z"
                  fill="#D9E5FF"
                />
                <text
                  x="20"
                  y="170"
                  fontFamily="Arial, sans-serif"
                  fontSize="80"
                  fontWeight="700"
                  fill="#D7D7D7"
                  opacity="0.6"
                >
                  03
                </text>
              </svg>
            </div>
            <div className={styles.savingsCard}>
              <div className={styles.savingsContent}>
                <h2 className={styles.savingsTitle}>Great Savings</h2>
                <p className={styles.savingsAddress}>{addressString}</p>
                <div className={styles.savingsAmount}>{savingsAmount}</div>
              </div>
            </div>
          </div>

          {/* Main Form Card - Now on RIGHT */}
          {/* Previous Contact Details card (kept for reference) */}
          {/*
            <div className={styles.mainCard}>
              <h1 className={styles.pageTitle}>Contact Details</h1>

              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.inputRow}>
                  <div className={styles.inputGroup}>
                    <label htmlFor="email" className={styles.label}>
                      Email Address <span className={styles.required}>*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      className={styles.input}
                      placeholder="Enter Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className={styles.inputGroup}>
                    <label htmlFor="phone" className={styles.label}>
                      Phone Number <span className={styles.required}>*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      className={styles.input}
                      placeholder="Enter Phone Number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className={styles.buttonRow}>
                  <button type="button" className={styles.formBackButton} onClick={handleBackClick}>
                    Back
                  </button>
                  <button type="submit" className={styles.continueButton} disabled={!email.trim() || !phone.trim()}>
                    Continue
                  </button>
                </div>
              </form>
            </div>
          */}

          <div className={styles.mainCard}>
            <h1 className={styles.pageTitle}>Select User Type</h1>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.userTypeGrid}>
                <button
                  type="button"
                  className={`${styles.userTypeCard} ${userType === "owner" ? styles.userTypeCardSelected : ""}`}
                  onClick={() => setUserType("owner")}
                  aria-pressed={userType === "owner"}
                >
                  <div className={styles.userTypeTitle}>Owner</div>
                  <div className={styles.userTypeSubtitle}>I own this property</div>
                </button>

                <button
                  type="button"
                  className={`${styles.userTypeCard} ${userType === "tenant" ? styles.userTypeCardSelected : ""}`}
                  onClick={() => setUserType("tenant")}
                  aria-pressed={userType === "tenant"}
                >
                  <div className={styles.userTypeTitle}>Tenant</div>
                  <div className={styles.userTypeSubtitle}>I rent this property</div>
                </button>
              </div>

              <label className={styles.mailingCheckboxRow}>
                <input
                  type="checkbox"
                  className={styles.mailingCheckbox}
                  checked={isMailingAddress}
                  onChange={(e) => setIsMailingAddress(e.target.checked)}
                />
                <span className={styles.mailingCheckboxLabel}>Set mailing address</span>
              </label>

              <div className={styles.buttonRow}>
                <button type="button" className={styles.formBackButton} onClick={handleBackClick}>
                  Back
                </button>
                <button
                  type="submit"
                  className={styles.continueButton}
                  disabled={!userType || isSubmitting}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
